import cx from 'classnames';
import { kebabCase } from 'lodash-es';
import type { FC, ReactNode, SyntheticEvent } from 'react';
import { Link } from 'react-router';

import useTracking from '@zen/utils/hooks/useTracking';
import { useNavigationHistory } from '@zen/utils/NavigationHistory';
import { extractToAndState } from '@zen/utils/routing';
import { createTrackingEvent, createTrackingLabelAttribute, createTrackingParentAttribute } from '@zen/utils/tracking';
import type { Modify } from '@zen/utils/typescript';

import Icon from '../../Icon';
import Tooltip from '../../Tooltip';
import Divider from '../Divider';
import type { MenuItemType } from '../types';

interface Props {
  item: Modify<MenuItemType, { onClick?: (event: SyntheticEvent) => void }>;
}

const MenuItem: FC<Props> = (props) => {
  const {
    className,
    disabled,
    disabledItemTooltip,
    highlighted,
    icon,
    isActive,
    label,
    linkTo,
    onClick,
    addDivider = false,
    openInNewTab
  } = props.item;
  const { navigate } = useNavigationHistory();
  const { trackEvent } = useTracking();

  const itemContent: ReactNode = (
    <>
      {icon && <Icon className="mr-2" icon={icon} />}
      <div className="text-sm leading-normal" {...createTrackingLabelAttribute()}>
        {label}
      </div>
      {isActive && <Icon className="absolute text-azure-base right-4" icon="zicon-tick" />}
    </>
  );

  const renderItem = (): ReactNode => {
    const classNames: string = cx(
      {
        'active:bg-azure-light hover:bg-azure-lightest cursor-pointer': !disabled,
        'text-grey-dark': !disabled && !highlighted,
        'text-grey-light cursor-not-allowed disabled': disabled
      },
      'flex py-1.5 leading-6 items-center pl-6 pr-14 font-normal my-1 relative',
      className
    );
    const itemContentClassNames: string = cx('flex items-center ', {
      'text-transparent bg-clip-text bg-sunset-base': highlighted
    });

    const handleClick = (event: SyntheticEvent): void => {
      if (disabled) {
        return;
      }
      const trackingEvent = createTrackingEvent('Design System/ContextMenu/MenuItem', 'click', event);

      trackEvent(trackingEvent);

      onClick?.(event);
    };

    if (linkTo && !disabled) {
      const target: string | undefined = openInNewTab ? '_blank' : undefined;

      if (openInNewTab) {
        return (
          <Link className={classNames} onClick={handleClick} target={target} {...extractToAndState(linkTo)}>
            <span className={itemContentClassNames}>{itemContent}</span>
          </Link>
        );
      }

      const handleInternalLinkClick = (event: SyntheticEvent) => {
        handleClick(event);
        if (typeof linkTo === 'string') {
          navigate(linkTo);
        } else {
          navigate(linkTo);
        }
      };

      return (
        <a className={classNames} onClick={handleInternalLinkClick}>
          <span className={itemContentClassNames}>{itemContent}</span>
        </a>
      );
    }

    return (
      <div className={classNames} onClick={handleClick}>
        <span className={itemContentClassNames}>{itemContent}</span>
      </div>
    );
  };

  const renderMenuItem = (): ReactNode => {
    if (disabled && disabledItemTooltip) {
      return (
        <Tooltip placement="left" tooltipContent={disabledItemTooltip}>
          {renderItem()}
        </Tooltip>
      );
    }

    return renderItem();
  };

  const testId: string = typeof label === 'string' ? `${kebabCase(label)}-menu-item` : 'context-menu-item';
  const classNames: string = cx(
    {
      disabled
    },
    'cursor-pointer'
  );

  return (
    <>
      <div
        className={classNames}
        data-component="context-menu-item"
        data-testid={testId}
        {...createTrackingParentAttribute('context-menu-item')}
      >
        {renderMenuItem()}
      </div>
      {addDivider && <Divider />}
    </>
  );
};

export default MenuItem;
